import { Anchor, Badge, Group, Modal, Stack, Text } from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { UnifyTable, type UnifyTableColumn, type UnifyTableMultiFilterConfig } from '@/components/UnifyTable';
import { formatDateTime } from './components/billingFormatters';
import { MOCK_BILLING_TASKS, type BillingTaskRow, type BillingTaskType } from './mock/mockBillingTasks';

const TYPE_LABELS: Record<BillingTaskType, string> = {
  'missing-data': 'Missing Data',
  'unsigned-note': 'Unsigned Note',
  'submission-failed': 'Submission Failed',
  'ack-rejected': '277CA Rejected',
  denial: 'Denial',
  'unmatched-era': 'Unmatched ERA',
  'payment-failed': 'Payment Failed',
};

const PRIORITY_COLORS = { high: 'red', medium: 'yellow', low: 'gray' } as const;
const STATUS_COLORS = { open: 'blue', 'in-progress': 'cyan', resolved: 'green' } as const;

export function OpsQueuePage(): JSX.Element {
  const [opened, { open, close }] = useDisclosure(false);
  const [selected, setSelected] = useState<BillingTaskRow | null>(null);

  const columns = useMemo(
    (): UnifyTableColumn<BillingTaskRow>[] => [
      {
        key: 'createdAt',
        label: 'Created',
        render: (row) => <Text size="sm">{formatDateTime(row.createdAt)}</Text>,
        getValue: (row) => row.createdAt,
      },
      {
        key: 'type',
        label: 'Type',
        render: (row) => <Text size="sm">{TYPE_LABELS[row.type]}</Text>,
        getValue: (row) => TYPE_LABELS[row.type],
      },
      {
        key: 'clientName',
        label: 'Client',
        render: (row) => <Text size="sm" fw={600}>{row.clientName}</Text>,
        getValue: (row) => row.clientName,
      },
      {
        key: 'summary',
        label: 'Summary',
        getValue: (row) => row.summary,
      },
      {
        key: 'priority',
        label: 'Priority',
        render: (row) => (
          <Badge variant="light" size="sm" color={PRIORITY_COLORS[row.priority]}>
            {row.priority}
          </Badge>
        ),
        getValue: (row) => row.priority,
      },
      {
        key: 'status',
        label: 'Status',
        render: (row) => (
          <Badge variant="light" size="sm" color={STATUS_COLORS[row.status]}>
            {row.status}
          </Badge>
        ),
        getValue: (row) => row.status,
      },
      {
        key: 'assignee',
        label: 'Assignee',
        getValue: (row) => row.assignee,
      },
    ],
    [],
  );

  const multiFilters = useMemo(
    (): UnifyTableMultiFilterConfig[] => [
      {
        columnKey: 'type',
        allLabel: 'All types',
        placeholder: 'Type',
        data: Object.values(TYPE_LABELS),
        emptyMeansAll: true,
      },
      {
        columnKey: 'status',
        allLabel: 'All statuses',
        placeholder: 'Status',
        data: ['open', 'in-progress', 'resolved'],
        emptyMeansAll: true,
      },
    ],
    [],
  );

  return (
    <>
      <UnifyTable
        title="Todos"
        subtitle="Billing exceptions requiring follow-up"
        columns={columns}
        data={MOCK_BILLING_TASKS}
        getRowKey={(row) => row.id}
        searchable
        searchPlaceholder="Search by client, summary, assignee…"
        sortable
        pagination
        defaultRowsPerPage={10}
        multiFilters={multiFilters}
        onRowClick={(row) => {
          setSelected(row);
          open();
        }}
      />

      <Modal opened={opened} onClose={close} title={selected?.summary ?? 'Task'} size="md" centered>
        {selected && (
          <Stack gap="md">
            <Group gap="xs">
              <Badge variant="light" color={PRIORITY_COLORS[selected.priority]}>
                {selected.priority}
              </Badge>
              <Badge variant="light" color={STATUS_COLORS[selected.status]}>
                {selected.status}
              </Badge>
            </Group>
            <Text size="sm">{selected.summary}</Text>
            <Text size="sm" c="dimmed">
              Client: {selected.clientName} · Assignee: {selected.assignee}
            </Text>
            {selected.claimId && (
              <Anchor
                component={Link}
                to={`/dashboard/billing/claims/${encodeURIComponent(selected.claimId)}`}
                size="sm"
                fw={600}
              >
                View claim
              </Anchor>
            )}
            {selected.linkTo && (
              <Anchor component={Link} to={selected.linkTo} size="sm" fw={600}>
                Go to related queue
              </Anchor>
            )}
          </Stack>
        )}
      </Modal>
    </>
  );
}
